import getVideoId from 'get-video-id';
import get from 'lodash/get';

export interface VideoDetails {
    snipped: {
        thumbnails: {
            maxres: {
                url: string;
            };
            high: {
                url: string;
            };
            medium: {
                url: string;
            };
            default: {
                url: string;
            };
        };
    };
}

export interface FetchedVideoDetails {
    thumbnail: string;
    ratio: number;
}

const getThumbnail = (videoDetails: VideoDetails) => {
    const thumbnails = get(videoDetails, 'snippet.thumbnails');
    let thumbnail = '';

    if (thumbnails) {
        const max = get(thumbnails, 'maxres.url');
        const high = get(thumbnails, 'high.url');
        const medium = get(thumbnails, 'medium.url');
        const standard = get(thumbnails, 'default.url');

        thumbnail = max || high || medium || standard;
    }

    return thumbnail;
};

const fetchYoutubeDetails = (videoId: string | undefined): Promise<FetchedVideoDetails> => {
    return fetch(
        `https://www.googleapis.com/youtube/v3/videos?id=${videoId}&key=AIzaSyBXRK1iQrNAjkHk2mfXI6hqwanhyEroenA&part=player,snippet,contentDetails&maxWidth=1280`,
    )
        .then((res) => res.json())
        .then((data) => {
            const thumbnail = getThumbnail(get(data, 'items[0]', {}));
            const width = get(data, 'items[0].player.embedWidth', 1280);
            const height = get(data, 'items[0].player.embedHeight', 720);
            const ratio = 100 / (width / height);

            return {
                thumbnail,
                ratio,
            };
        });
};

const fetchVimeoDetails = (videoId: string | undefined): Promise<FetchedVideoDetails> => {
    return fetch(`https://vimeo.com/api/oembed.json?url=https://vimeo.com/${videoId}`)
        .then((res) => res.json())
        .then((data) => {
            const thumbnail = data.thumbnail_url;
            const width = get(data, 'width', 1280);
            const height = get(data, 'height', 720);
            const ratio = 100 / (width / height);

            return {
                thumbnail,
                ratio,
            };
        });
};

/**
 * Extracts video ID and private hash from Vimeo URLs with private hash format.
 * Format: https://vimeo.com/<VIDEO_ID>/<PRIVATE_HASH>
 * @returns Combined path "VIDEO_ID/PRIVATE_HASH" or null if not a private Vimeo URL
 */
const extractVimeoPrivateHash = (url: string): string | null => {
    const privateVimeoUrlPattern = /^https:\/\/vimeo\.com\/(\d+)\/([a-zA-Z0-9]+)$/;
    const match = url.match(privateVimeoUrlPattern);

    if (match && match.length === 3) {
        return `${match[1]}/${match[2]}`;
    }

    return null;
};

export const fetchVideoDetails = (url: string): Promise<FetchedVideoDetails> | undefined => {
    const { service, id } = getVideoId(url);

    if (service === 'youtube') return fetchYoutubeDetails(id);

    if (service === 'vimeo') {
        // Check for private Vimeo URLs first (they have a different format)
        const privateVimeoPath = extractVimeoPrivateHash(url);

        if (privateVimeoPath) {
            return fetchVimeoDetails(privateVimeoPath);
        }

        return fetchVimeoDetails(id);
    }
};
